import { Phone, PhoneIncoming, PhoneOutgoing } from "lucide-react";
import { cn } from "@tellescope/ui";
import { Tooltip } from "@tellescope/design-system";
import { formatPhone } from "@tellescope/utils";
import { usePhoneNumberLabel } from "../../../hooks/calling/useTwilioNumbers";
import { CallTimer } from "./CallTimer";
import { CallDirection, CallStatus } from "../../../store";

export interface CallHeaderProps {
  /** Call direction */
  direction: CallDirection | null;
  /** Current call status */
  status: CallStatus;
  /** Status label to display */
  statusLabel: string;
  /** Call start time for timer */
  startTime: Date | null;
  /** Organization's Twilio number used for the call */
  orgNumber?: string | null;
  /** Additional className for the container */
  className?: string;
}

/**
 * CallHeader - Header section for the call window.
 *
 * Displays call direction icon, status, and duration timer.
 *
 * @example
 * ```tsx
 * <CallHeader
 *   direction="inbound"
 *   status="open"
 *   statusLabel="Connected"
 *   startTime={callStartTime}
 * />
 * ```
 */
export function CallHeader({ direction, status, statusLabel, startTime, orgNumber, className }: CallHeaderProps) {
  const getPhoneLabel = usePhoneNumberLabel();

  // Determine which icon to show based on direction
  const DirectionIcon = direction === "inbound" ? PhoneIncoming : direction === "outbound" ? PhoneOutgoing : Phone;

  // Get org line label
  const orgLabel = orgNumber ? getPhoneLabel(orgNumber) : null;

  // Determine status color
  const getStatusColor = () => {
    switch (status) {
      case "open":
        return "text-green-600";
      case "connecting":
      case "ringing":
        return "text-yellow-600";
      case "disconnected":
        return "text-red-600";
      default:
        return "text-muted-foreground";
    }
  };

  return (
    <div className={cn("flex items-center justify-between px-4 py-3 bg-muted/50", className)}>
      <div className="flex items-center gap-2">
        <DirectionIcon className={cn("h-4 w-4", getStatusColor())} />
        <span className={cn("text-sm font-medium", getStatusColor())}>{statusLabel}</span>
        {orgLabel && (
          <span className="text-xs text-muted-foreground">
            ·{" "}
            {orgLabel !== orgNumber ? (
              <Tooltip>
                <Tooltip.Trigger asChild>
                  <span className="cursor-help border-b border-dotted border-muted-foreground/50">{orgLabel}</span>
                </Tooltip.Trigger>
                <Tooltip.Content>{formatPhone(orgNumber)}</Tooltip.Content>
              </Tooltip>
            ) : (
              formatPhone(orgNumber)
            )}
          </span>
        )}
      </div>

      {/* Show timer when call is connected */}
      {status === "open" && <CallTimer startTime={startTime} className="text-sm text-muted-foreground font-mono" />}
    </div>
  );
}
